/**
 * SocialProof — inline badges that turn a story card into a "hot" story.
 *
 * Renders `TRENDING`, `MOST READ`, `LIVE` based on the badge kind passed in.
 * Deliberately tiny — a chip you drop next to the eyebrow on any article
 * card without redesigning the card.
 *
 * The parent decides *which* badge to show based on data (view_count, position
 * in list, etc.). This component is purely presentation.
 */
import { Flame, Eye, TrendingUp, MessageSquare, Radio } from "lucide-react";

const VARIANTS = {
  trending: { Icon: Flame, label: "TRENDING", cls: "text-titans-red border-titans-red/40 bg-titans-red/10" },
  mostRead: { Icon: TrendingUp, label: "MOST READ TODAY", cls: "text-titans-blue border-titans-blue/40 bg-titans-blue/10" },
  popular: { Icon: Eye, label: "POPULAR", cls: "text-amber-400 border-amber-400/40 bg-amber-400/10" },
  discussed: { Icon: MessageSquare, label: "MOST DISCUSSED", cls: "text-emerald-400 border-emerald-400/40 bg-emerald-400/10" },
  live: { Icon: Radio, label: "LIVE", cls: "text-titans-red border-titans-red/40 bg-titans-red/10 animate-pulse" },
};

export function SocialProofBadge({ kind = "trending", className = "" }) {
  const v = VARIANTS[kind] || VARIANTS.trending;
  const Icon = v.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 border font-cond uppercase tracking-wider text-[9px] ${v.cls} ${className}`}
      data-testid={`social-proof-${kind}`}
    >
      <Icon size={9} /> {v.label}
    </span>
  );
}

/**
 * SocialProofRow — packaged strip that decides which badges to show for a
 * given article based on its data. Feed the article + its position in the
 * most-read list.
 */
export function SocialProofRow({ article, mostReadIds = [], className = "" }) {
  if (!article) return null;
  const isMostRead = mostReadIds.includes(article.id);
  const hot = (article.view_count || 0) > 500;
  const breaking = (article.category || "").toLowerCase().includes("breaking");
  const badges = [];
  if (breaking) badges.push("live");
  else if (isMostRead) badges.push("mostRead");
  else if (hot) badges.push("trending");
  if (badges.length === 0) return null;
  return (
    <div className={`flex items-center gap-1 ${className}`} data-testid="social-proof-row">
      {badges.map((k) => <SocialProofBadge key={k} kind={k} />)}
    </div>
  );
}
